import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Checkbox, Slider } from 'antd';
// import gtLogo from '../../../../assets/icons/GrantThornton_logo_2.png';
import {
  SubsidyTypeCode,
  EligibleExpense,
  FundingSource,
  InvestmentActivity,
  ProgramFilterStatus,
} from 'utils/enums';
import { Constants } from 'utils/constants';
import { DatePicker } from 'components';
import { RegionAutocomplete } from '../../components';
import { useRegionHQOptionsQuery } from '../useRegionHQOptionsQuery';
import { useRegionUnityOptionsQuery } from '../useRegionUnityOptionsQuery';
import { useMunicipalityOptionsQuery } from '../useMunicipalityOptionsQuery';
import { useFilterRegionAutomaticClear } from '../useFilterRegionAutomaticClear';

/**
 * @hook
 *
 * @summary
 * Manages all state, business logic, and UI configuration for the Funding page Sidebar filters.
 *
 * @description
 * Implements the **Container** side of the Presentation–Container pattern.
 * This hook encapsulates:
 * - Filter state management using `useReducer`
 * - Memoized callbacks for updating, toggling, and resetting filters
 * - Complete filter items configuration with pre-rendered JSX components
 * - Helper functions for rendering checkboxes and radio groups
 *
 * The `useFundingFilters` hook is completely decoupled from presentation concerns.
 * It returns a fully configured `items` array ready for Ant Design's `Collapse` component,
 * along with action handlers. The consuming component simply renders the provided structure
 * without knowledge of filter logic or state management.
 *
 * @param {Object} props - Hook props.
 * @returns {Object} Hook return values.
 * @returns {Array<Object>} return.items - Pre-configured collapse items for Ant Design Collapse component.
 * Each item contains key, label, and children (rendered filter components).
 * @returns {Array<Object>} return.filters - Applied filters.
 * @returns {() => void} return.clearFilters - Resets all filters to their initial state.
 *
 * @example
 * const { items, clearFilters, filters } = useFundingFilters();
 *
 * return (
 *   <>
 *     <Collapse items={items} />
 *     <Button onClick={clearFilters}>Καθαρισμός Φίλτρων</Button>
 *   </>
 * );
 */
export function useFundingFilters() {
  const [disableAll, setDisableAll] = useState(false);
  const [disableCategories, setDisableCategories] = useState(false);
  const [filters, setFilters] = useReducer(reducer, initialFilters);

  const regionHQQuery = useRegionHQOptionsQuery({
    search: filters.regionHQ,
    selected: filters.selectedRegionHQ,
  });
  const regionUnityQuery = useRegionUnityOptionsQuery({
    regions: filters.selectedRegionHQ,
    search: filters.regionUnity,
    selected: filters.selectedRegionUnity,
  });
  const municipalityQuery = useMunicipalityOptionsQuery({
    regions: filters.selectedRegionHQ,
    regionalUnits: filters.selectedRegionUnity,
    search: filters.municipality,
    selected: filters.selectedMunicipality,
  });

  const {
    handleRemoveMunicipality,
    handleRemoveRegionalUnit,
    handleRemoveRegion,
  } = useFilterRegionAutomaticClear({
    selectedRegions: filters.selectedRegionHQ,
    selectedRegionalUnits: filters.selectedRegionUnity,
    selectedMunicipalities: filters.selectedMunicipality,
    setFilters,
  });

  const isSubsidyCheckLoanEnabled = useMemo(
    () => filters.subsidyCheck1 === true,
    [filters.subsidyCheck1]
  );

  /**
   * @summary If Loan enabled only then enable the sliderFinancing cause it is based on loan.
   */
  useEffect(() => {
    if (isSubsidyCheckLoanEnabled === false) {
      setFilters({
        type: 'SET_SLIDER_FINANCING',
        value: initialFilters.sliderFinancing,
      });
    }
  }, [isSubsidyCheckLoanEnabled]);

  const areRegionalUnitsDisabled = useMemo(
    () => filters.selectedRegionHQ.length === 0,
    [filters.selectedRegionHQ]
  );

  const areMunicipalitiesDisabled = useMemo(
    () => filters.selectedRegionUnity.length === 0,
    [filters.selectedRegionUnity]
  );

  const clearFilters = useCallback((isPagination = false) => {
    if (isPagination === true) {
      setFilters({ type: 'CLEAR_FILTERS_PAGINATED' });
    } else {
      setFilters({ type: 'CLEAR_FILTERS' });
    }
  }, []);

  /**
   * @summary Exits file search by removing file, really means re-enable all filters.
   */
  const exitFileSearch = useCallback(() => {
    setDisableAll(false);
    clearFilters();
  }, [clearFilters, setDisableAll]);

  /**
   * @summary Exits filter search by adding file search, really means disable all filters.
   */
  const exitFilterSearch = useCallback(() => {
    setDisableAll(true);
    clearFilters();
  }, [clearFilters]);

  const updatePaginatedFilterCategory = useCallback(
    category => {
      const newFilters = {
        ...filters,
        // fundingInsights: false,
        expired: false,
        closeToExpire: false,
        openProgram: false,
        [category]: true,
      };
      setFilters({ type: 'TOGGLE_SPECIFIC_CATEGORY', value: category });
      setDisableCategories(true);
      return newFilters;
    },
    [filters, setFilters, setDisableCategories]
  );

  /**
   * @summary Exits pagination really means re-enable category filters.
   */
  const exitPagination = useCallback(() => {
    setDisableCategories(false);
    clearFilters();
  }, [clearFilters]);

  const renderCheckboxes = useCallback(
    checkboxes =>
      checkboxes.map(
        ({ key, label, value, src, actionType, disabled = false }) => (
          <Checkbox
            disabled={disableAll || disabled}
            key={key}
            checked={filters[value]}
            onChange={() =>
              setFilters({ type: actionType, value: !filters[value] })
            }
          >
            {src ? <img src={src} className="mr-1" alt="Logo" /> : undefined}
            {label}
          </Checkbox>
        )
      ),
    [filters, disableAll]
  );

  const items = [
    {
      key: '1',
      label: 'Κατάσταση',
      children: (
        <div className="checkboxes-filter-wrapper">
          {renderCheckboxes([
            {
              key: ProgramFilterStatus.OPEN_PROGRAM.value,
              label: ProgramFilterStatus.OPEN_PROGRAM.label,
              value: ProgramFilterStatus.OPEN_PROGRAM.value,
              actionType: 'TOGGLE_OPEN_PROGRAM',
              disabled: disableCategories,
            },
            {
              key: ProgramFilterStatus.CLOSE_TO_EXPIRE.value,
              label: ProgramFilterStatus.CLOSE_TO_EXPIRE.label,
              value: ProgramFilterStatus.CLOSE_TO_EXPIRE.value,
              actionType: 'TOGGLE_CLOSE_TO_EXPIRE',
              disabled: disableCategories,
            },
            {
              key: ProgramFilterStatus.EXPIRED.value,
              label: ProgramFilterStatus.EXPIRED.label,
              value: ProgramFilterStatus.EXPIRED.value,
              actionType: 'TOGGLE_EXPIRED',
              disabled: disableCategories,
            },
            // {
            //   key: ProgramFilterStatus.FUNDING_INSIGHTS.value,
            //   label: (
            //     <>
            //       <img src={gtLogo} alt="grant Thornton Logo" />
            //       &nbsp;&nbsp;{ProgramFilterStatus.FUNDING_INSIGHTS.label}
            //     </>
            //   ),
            //   value: ProgramFilterStatus.FUNDING_INSIGHTS.value,
            //   actionType: 'TOGGLE_FUNDING_INSIGHTS',
            //   disabled: disableCategories,
            // },
          ])}
        </div>
      ),
    },
    {
      key: '2',
      label: 'Πηγή Χρηματοδότησης',
      children: (
        <div className="checkboxes-filter-wrapper">
          {renderCheckboxes(
            Object.values(FundingSource).map(source => ({
              key: source.value,
              label: source.label,
              value: source.value,
              actionType: source.actionType,
            }))
          )}
        </div>
      ),
    },
    {
      key: '4',
      label: 'Δραστηριότητα Επένδυσης',
      children: (
        <div className="checkboxes-filter-wrapper">
          {renderCheckboxes(
            Object.values(InvestmentActivity).map(activity => ({
              key: activity.value,
              label: activity.label,
              value: activity.value,
              actionType: activity.actionType,
            }))
          )}
        </div>
      ),
    },
    {
      key: '5',
      label: 'Περιοχή Επένδυσης',
      children: (
        <div className="multiple-searcbars-filter-wrapper">
          <RegionAutocomplete
            disabled={disableAll}
            query={regionHQQuery}
            filters={filters}
            setFilters={setFilters}
            placeholder="Αναζήτησε Περιφέρεια Έδρας"
            searchType="regionHQ"
            selectedType="selectedRegionHQ"
            searchTypeAction="SET_REGION_HQ"
            selectedTypeAction="SET_SELECTED_REGION_HQ"
            onDeleteItem={handleRemoveRegion}
          />
          <RegionAutocomplete
            disabled={areRegionalUnitsDisabled || disableAll}
            query={regionUnityQuery}
            filters={filters}
            setFilters={setFilters}
            placeholder="Αναζήτησε Περιφερειακή Ενότητα"
            searchType="regionUnity"
            selectedType="selectedRegionUnity"
            searchTypeAction="SET_REGION_UNITY"
            selectedTypeAction="SET_SELECTED_REGION_UNITY"
            onDeleteItem={handleRemoveRegionalUnit}
          />
          <RegionAutocomplete
            disabled={areMunicipalitiesDisabled || disableAll}
            query={municipalityQuery}
            filters={filters}
            setFilters={setFilters}
            placeholder="Αναζήτησε Δήμο"
            searchType="municipality"
            selectedType="selectedMunicipality"
            searchTypeAction="SET_MUNICIPALITY"
            selectedTypeAction="SET_SELECTED_MUNICIPALITY"
            onDeleteItem={handleRemoveMunicipality}
          />
        </div>
      ),
    },
    {
      key: '6',
      label: 'Δαπάνες',
      children: (
        <div className="checkboxes-filter-wrapper">
          {renderCheckboxes(
            Object.values(EligibleExpense).map(expense => ({
              key: expense.value,
              label: expense.label,
              value: expense.value,
              actionType: expense.actionType,
            }))
          )}
        </div>
      ),
    },
    {
      key: '7',
      label: 'Είδος Ενίσχυσης',
      children: (
        <div className="checkboxes-filter-wrapper">
          {renderCheckboxes(
            Object.values(SubsidyTypeCode).map(
              ({ label, value, actionType }) => ({
                key: value,
                label,
                value,
                actionType,
              })
            )
          )}
        </div>
      ),
    },
    {
      key: '8',
      label: 'Ποσοστό Ενίσχυσης',
      children: (
        <div className="slider-filter-wrapper">
          <span>{filters.subsidy[0]}%</span>
          <Slider
            disabled={disableAll}
            range
            value={filters.subsidy}
            onChange={value => setFilters({ type: 'SET_SUBSIDY', value })}
          />
          <span>{filters.subsidy[1]}%</span>
        </div>
      ),
    },
    {
      key: '9',
      label: 'Μέγιστη Επιχορήγηση(€)',
      children: (
        <div className="slider-filter-wrapper">
          <span className="slider-filter-content">
            {adjustBigNumberToString(filters.maxSubsidy[0])}
          </span>
          <Slider
            range
            min={0}
            max={1000000}
            step={10000}
            disabled={disableAll}
            value={filters.maxSubsidy}
            onChange={value => setFilters({ type: 'SET_MAX_SUBSIDY', value })}
          />
          <span className="slider-filter-content">
            {adjustBigNumberToString(filters.maxSubsidy[1])}
          </span>
        </div>
      ),
    },
    {
      key: '10',
      label: 'Μέγιστη Χρηματοδότηση(Δάνειο)(€)',
      children: (
        <div className="slider-filter-wrapper">
          <span className="slider-filter-content">
            {adjustBigNumberToString(filters.sliderFinancing[0])}
          </span>
          <Slider
            range
            min={0}
            max={1000000}
            step={10000}
            disabled={disableAll || !isSubsidyCheckLoanEnabled}
            value={filters.sliderFinancing}
            onChange={value =>
              setFilters({ type: 'SET_SLIDER_FINANCING', value })
            }
          />
          <span className="slider-filter-content">
            {adjustBigNumberToString(filters.sliderFinancing[1])}
          </span>
        </div>
      ),
    },
    {
      key: '11',
      label: 'Ημερ/νίες (Έναρξη / Λήξης)',
      children: (
        <div className="datePicker-filter-wrapper">
          <DatePicker
            disabled={disableAll}
            allowClear
            placeholder={Constants.from}
            value={filters.dateFrom}
            onChange={date =>
              setFilters({ type: 'SET_DATE_FROM', value: date })
            }
          />
          <DatePicker
            disabled={disableAll}
            allowClear
            placeholder={Constants.to}
            value={filters.dateTo}
            onChange={date => setFilters({ type: 'SET_DATE_TO', value: date })}
          />
        </div>
      ),
    },
  ];

  return {
    filters,
    items,
    clearFilters,
    updatePaginatedFilterCategory,
    exitPagination,
    exitFilterSearch,
    exitFileSearch,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_EXPENSE_CHECK1':
      return { ...state, expenseCheck1: action.value };
    case 'TOGGLE_EXPENSE_CHECK2':
      return { ...state, expenseCheck2: action.value };
    case 'TOGGLE_EXPENSE_CHECK3':
      return { ...state, expenseCheck3: action.value };
    case 'TOGGLE_EXPENSE_CHECK4':
      return { ...state, expenseCheck4: action.value };
    case 'TOGGLE_EXPENSE_CHECK5':
      return { ...state, expenseCheck5: action.value };
    case 'TOGGLE_EXPENSE_CHECK6':
      return { ...state, expenseCheck6: action.value };
    case 'TOGGLE_EXPENSE_CHECK7':
      return { ...state, expenseCheck7: action.value };
    case 'TOGGLE_EXPENSE_CHECK8':
      return { ...state, expenseCheck8: action.value };
    case 'TOGGLE_EXPENSE_CHECK9':
      return { ...state, expenseCheck9: action.value };
    case 'TOGGLE_EXPENSE_CHECK10':
      return { ...state, expenseCheck10: action.value };
    case 'TOGGLE_EXPENSE_CHECK11':
      return { ...state, expenseCheck11: action.value };
    case 'TOGGLE_EXPENSE_CHECK12':
      return { ...state, expenseCheck12: action.value };
    case 'TOGGLE_EXPENSE_CHECK13':
      return { ...state, expenseCheck13: action.value };
    case 'TOGGLE_EXPENSE_CHECK14':
      return { ...state, expenseCheck14: action.value };
    case 'TOGGLE_ACTIVITY_CHECK1':
      return { ...state, activityCheck1: action.value };
    case 'TOGGLE_ACTIVITY_CHECK2':
      return { ...state, activityCheck2: action.value };
    case 'TOGGLE_ACTIVITY_CHECK3':
      return { ...state, activityCheck3: action.value };
    case 'TOGGLE_ACTIVITY_CHECK4':
      return { ...state, activityCheck4: action.value };
    case 'TOGGLE_ACTIVITY_CHECK5':
      return { ...state, activityCheck5: action.value };
    case 'TOGGLE_ACTIVITY_CHECK6':
      return { ...state, activityCheck6: action.value };
    case 'TOGGLE_ACTIVITY_CHECK7':
      return { ...state, activityCheck7: action.value };
    case 'TOGGLE_ACTIVITY_CHECK8':
      return { ...state, activityCheck8: action.value };
    case 'TOGGLE_ACTIVITY_CHECK9':
      return { ...state, activityCheck9: action.value };
    case 'TOGGLE_ACTIVITY_CHECK10':
      return { ...state, activityCheck10: action.value };
    case 'TOGGLE_ACTIVITY_CHECK11':
      return { ...state, activityCheck11: action.value };
    case 'TOGGLE_ACTIVITY_CHECK12':
      return { ...state, activityCheck12: action.value };
    case 'TOGGLE_ACTIVITY_CHECK13':
      return { ...state, activityCheck13: action.value };
    case 'TOGGLE_ACTIVITY_CHECK14':
      return { ...state, activityCheck14: action.value };
    case 'TOGGLE_ACTIVITY_CHECK15':
      return { ...state, activityCheck15: action.value };
    case 'TOGGLE_ACTIVITY_CHECK16':
      return { ...state, activityCheck16: action.value };
    case 'TOGGLE_ACTIVITY_CHECK17':
      return { ...state, activityCheck17: action.value };
    case 'TOGGLE_ACTIVITY_CHECK18':
      return { ...state, activityCheck18: action.value };
    case 'TOGGLE_ACTIVITY_CHECK19':
      return { ...state, activityCheck19: action.value };
    case 'TOGGLE_ACTIVITY_CHECK20':
      return { ...state, activityCheck20: action.value };
    case 'TOGGLE_ACTIVITY_CHECK21':
      return { ...state, activityCheck21: action.value };
    case 'TOGGLE_ACTIVITY_CHECK22':
      return { ...state, activityCheck22: action.value };
    case 'SET_PROGRAM':
      return { ...state, program: action.value };
    case 'SET_REGION_HQ':
      return { ...state, regionHQ: action.value };
    case 'SET_REGION_UNITY':
      return { ...state, regionUnity: action.value };
    case 'SET_MUNICIPALITY':
      return { ...state, municipality: action.value };
    case 'SET_SELECTED_REGION_HQ':
      return { ...state, selectedRegionHQ: action.value };
    case 'SET_SELECTED_REGION_UNITY':
      return { ...state, selectedRegionUnity: action.value };
    case 'SET_SELECTED_MUNICIPALITY':
      return { ...state, selectedMunicipality: action.value };
    case 'SET_REGION_UNITY_OPTIONS':
      return { ...state, regionUnityOptions: action.value };
    case 'SET_MUNICIPALITY_OPTIONS':
      return { ...state, municipalityOptions: action.value };
    case 'SET_SLIDER_FINANCING':
      return { ...state, sliderFinancing: action.value };
    case 'SET_SUBSIDY':
      return { ...state, subsidy: action.value };
    case 'SET_MAX_SUBSIDY':
      return { ...state, maxSubsidy: action.value };
    case 'SET_DATE_FROM':
      return { ...state, dateFrom: action.value };
    case 'SET_DATE_TO':
      return { ...state, dateTo: action.value };
    case 'TOGGLE_OPEN_PROGRAM':
      return { ...state, openProgram: action.value };
    case 'TOGGLE_CLOSE_TO_EXPIRE':
      return { ...state, closeToExpire: action.value };
    case 'TOGGLE_EXPIRED':
      return { ...state, expired: action.value };
    // case 'TOGGLE_FUNDING_INSIGHTS':
    //   return { ...state, fundingInsights: action.value };
    case 'TOGGLE_SPECIFIC_CATEGORY':
      return {
        ...state,
        // fundingInsights: false,
        expired: false,
        closeToExpire: false,
        openProgram: false,
        [action.value]: true,
      };
    case 'TOGGLE_ESPA':
      return {
        ...state,
        espa: action.value,
        taa: action.value === true ? false : state.taa,
      };
    case 'TOGGLE_TAA':
      return {
        ...state,
        taa: action.value,
        espa: action.value === true ? false : state.espa,
      };
    case 'TOGGLE_SUBSIDY_CHECK1': // Δάνειο, enable Μέγιστη Χρηματοδότηση if true, else disable it
      return { ...state, subsidyCheck1: action.value };
    case 'TOGGLE_SUBSIDY_CHECK2':
      return { ...state, subsidyCheck2: action.value };
    case 'CLEAR_FILTERS_PAGINATED':
      return {
        ...initialFilters,
        // fundingInsights: state.fundingInsights,
        expired: state.expired,
        closeToExpire: state.closeToExpire,
        openProgram: state.openProgram,
      };
    case 'CLEAR_FILTERS':
      return initialFilters;
    default:
      return state;
  }
}

const initialFilters = {
  expenseCheck1: false,
  expenseCheck2: false,
  expenseCheck3: false,
  expenseCheck4: false,
  expenseCheck5: false,
  expenseCheck6: false,
  expenseCheck7: false,
  expenseCheck8: false,
  expenseCheck9: false,
  expenseCheck10: false,
  expenseCheck11: false,
  expenseCheck12: false,
  expenseCheck13: false,
  expenseCheck14: false,
  activityCheck1: false,
  activityCheck2: false,
  activityCheck3: false,
  activityCheck4: false,
  activityCheck5: false,
  activityCheck6: false,
  activityCheck7: false,
  activityCheck8: false,
  activityCheck9: false,
  activityCheck10: false,
  activityCheck11: false,
  activityCheck12: false,
  activityCheck13: false,
  activityCheck14: false,
  activityCheck15: false,
  activityCheck16: false,
  activityCheck17: false,
  activityCheck18: false,
  activityCheck19: false,
  activityCheck20: false,
  activityCheck21: false,
  activityCheck22: false,
  program: '',
  regionHQ: '',
  regionUnity: '',
  municipality: '',
  selectedRegionHQ: [],
  selectedRegionUnity: [],
  selectedMunicipality: [],
  regionUnityOptions: [],
  municipalityOptions: [],
  sliderFinancing: [0, 1000000],
  subsidy: [0, 100],
  maxSubsidy: [0, 1000000],
  dateFrom: null,
  dateTo: null,
  openProgram: false,
  closeToExpire: false,
  expired: false,
  // fundingInsights: false,
  espa: false,
  taa: false,
  subsidyCheck1: false,
  subsidyCheck2: false,
};

function adjustBigNumberToString(num) {
  if (num === 1000000) {
    const millions = num / 1000000;
    return `${Math.round(millions)}εκ.`;
  } else if (num >= 1000) {
    const thousands = num / 1000;
    return `${Math.round(thousands)}χιλ.`;
  } else if (num !== 0) {
    return `${num}`;
  } else {
    return 0;
  }
}
